import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createDocRequest,
  getAdminDocRequests,
  getAdminDocRequestDetails,
  getVendorDocRequests,
  uploadVendorDoc,
  reviewVendorDoc,
  getAdminVendorDocRequests,
  addVendorsToDocRequest,
  getVendorsList,
} from "./documentRequestsApi";
import { message } from "antd";

const initialState = {
  adminRequests: [],
  requestDetails: null,
  vendorRequests: [],
  loading: false,
};

const actions = {
  CREATE_REQUEST: "documentRequests/CREATE_REQUEST",
  GET_ADMIN_REQUESTS: "documentRequests/GET_ADMIN_REQUESTS",
  GET_ADMIN_DETAILS: "documentRequests/GET_ADMIN_DETAILS",
  GET_VENDOR_REQUESTS: "documentRequests/GET_VENDOR_REQUESTS",
  GET_ADMIN_VENDOR_REQUESTS: "documentRequests/GET_ADMIN_VENDOR_REQUESTS",
  UPLOAD_DOC: "documentRequests/UPLOAD_DOC",
  REVIEW_DOC: "documentRequests/REVIEW_DOC",
  ADD_VENDORS: "documentRequests/ADD_VENDORS",
};

export const createRequestThunk = createAsyncThunk(
  actions.CREATE_REQUEST,
  async (payload) => {
    const response = await createDocRequest(payload);
    return response;
  }
);

export const getAdminRequestsThunk = createAsyncThunk(
  actions.GET_ADMIN_REQUESTS,
  async () => {
    const response = await getAdminDocRequests();
    return response;
  }
);

export const getAdminRequestDetailsThunk = createAsyncThunk(
  actions.GET_ADMIN_DETAILS,
  async (id) => {
    const response = await getAdminDocRequestDetails(id);
    return response;
  }
);

export const getVendorRequestsThunk = createAsyncThunk(
  actions.GET_VENDOR_REQUESTS,
  async () => {
    const response = await getVendorDocRequests();
    return response;
  }
);

export const getAdminVendorRequestsThunk = createAsyncThunk(
  actions.GET_ADMIN_VENDOR_REQUESTS,
  async (vendorId) => {
    const response = await getAdminVendorDocRequests(vendorId);
    return response;
  }
);

export const uploadVendorDocThunk = createAsyncThunk(
  actions.UPLOAD_DOC,
  async (payload, { rejectWithValue }) => {
    const response = await uploadVendorDoc(payload);
    if (!response || response.success === false || response.errorMessage) {
      return rejectWithValue(response);
    }
    return response;
  }
);

export const reviewVendorDocThunk = createAsyncThunk(
  actions.REVIEW_DOC,
  async (payload) => {
    const response = await reviewVendorDoc(payload);
    return response;
  }
);

export const addVendorsToRequestThunk = createAsyncThunk(
  actions.ADD_VENDORS,
  async ({ id, vendor_ids }) => {
    const response = await addVendorsToDocRequest(id, { vendor_ids });
    return response;
  }
);

export const getVendorsListThunk = createAsyncThunk(
  "documentRequests/GET_VENDORS_LIST",
  async () => {
    const response = await getVendorsList();
    return response;
  }
);

export const documentRequestsSlice = createSlice({
  name: "documentRequests",
  initialState,
  reducers: {
    clearDetails: (state) => {
      state.requestDetails = null;
    },
  },
  extraReducers: (builder) => {
    // Create Request
    builder
      .addCase(createRequestThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(createRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg || "Request created successfully");
        } else {
          message.error(msg || "Failed to create request");
        }
      })
      .addCase(createRequestThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to create request");
      });

    // Get Admin Requests
    builder
      .addCase(getAdminRequestsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAdminRequestsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload;
        if (success) {
          state.adminRequests = data;
        } else {
          message.error(msg || "Failed to fetch document requests");
        }
      })
      .addCase(getAdminRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to fetch document requests");
      });

    // Get Admin Details
    builder
      .addCase(getAdminRequestDetailsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAdminRequestDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload;
        if (success) {
          state.requestDetails = data;
        } else {
          message.error(msg || "Failed to fetch details");
        }
      })
      .addCase(getAdminRequestDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to fetch details");
      });

    // Get Vendor Requests
    builder
      .addCase(getVendorRequestsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVendorRequestsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload;
        if (success) {
          state.vendorRequests = data;
        } else {
          message.error(msg || "Failed to fetch requests");
        }
      })
      .addCase(getVendorRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to fetch requests");
      });

    // Get Admin Vendor Requests
    builder
      .addCase(getAdminVendorRequestsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAdminVendorRequestsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload;
        if (success) {
          state.vendorRequests = data;
        } else {
          message.error(msg || "Failed to fetch vendor requests");
        }
      })
      .addCase(getAdminVendorRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to fetch vendor requests");
      });

    // Upload Vendor Doc
    builder
      .addCase(uploadVendorDocThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadVendorDocThunk.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(uploadVendorDocThunk.rejected, (state, action) => {
        state.loading = false;
      });

    // Review Vendor Doc
    builder
      .addCase(reviewVendorDocThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(reviewVendorDocThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg || "Review updated successfully");
        } else {
          message.error(msg || "Failed to update review");
        }
      })
      .addCase(reviewVendorDocThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to update review");
      });

    // Add Vendors
    builder
      .addCase(addVendorsToRequestThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(addVendorsToRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg || "Vendors added successfully");
        } else {
          message.error(msg || "Failed to add vendors");
        }
      })
      .addCase(addVendorsToRequestThunk.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message || "Failed to add vendors");
      });
  },
});

export const { clearDetails } = documentRequestsSlice.actions;
export default documentRequestsSlice.reducer;
